import { IAMapWebServiceSetting, ICommonSettings } from "@kuika/kuika-cl-model";
import { Form, FormInstance } from "antd/es";
import { Guid } from "guid-typescript";
import * as React from "react";
import { IWithSDProps } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../../shared/utility/save-status";
import { DefaultModal } from "../../../../common/default-modal";
import { type KuikaButtonStatus } from "../../../../ui-kit/kuika-button/kuika-button";
import KuikaInput from "../../../../ui-kit/kuika-input/kuika-input";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigModalBody, ConfigModalFooter } from "../config-modal-parts";
import { ConfigField } from "../config-pane-parts";

interface IAMapWebServiceFormData {
  name: string;
  apiKey: string;
}

export interface IConfigAMapWebServiceSettingsState {
  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  /** Where Delete stands, on its own button. */
  deleteStatus: KuikaButtonStatus;
  editingConfig?: IAMapWebServiceSetting;
}

export interface IConfigAMapWebServiceSettingsProps {
  formInputName: string[];
  closeModal: () => void;
  showModal: boolean;
  editingAMapWebServiceId?: string;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => React.ReactNode;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

export class ConfigAMapWebServiceSettings extends React.PureComponent<
  IConfigAMapWebServiceSettingsProps,
  IConfigAMapWebServiceSettingsState
> {
  formRef = React.createRef<FormInstance>();
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  /** Delete has its own machine: the control that was pressed is the one that reports. */
  private readonly deleteStatus: SaveStatus = new SaveStatus((deleteStatus: KuikaButtonStatus): void => {
    this.setState({ deleteStatus });
  });

  constructor(props: IConfigAMapWebServiceSettingsProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      deleteStatus: "idle"
    };
  }

  componentDidMount(): void {
    this.loadSetting();
  }

  componentWillUnmount() {
    this.saveStatus.dispose();
    this.deleteStatus.dispose();
  }

  componentDidUpdate(prevProps: IConfigAMapWebServiceSettingsProps & IWithSDProps): void {
    if (prevProps.editingAMapWebServiceId !== this.props.editingAMapWebServiceId) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    } else if (this.props.showModal !== prevProps.showModal) {
      this.saveStatus.reset();
      this.deleteStatus.reset();
      this.loadSetting();
    }
  }

  loadSetting = (): void => {
    if (!this.props.editingAMapWebServiceId) return;

    const editingConfig: IAMapWebServiceSetting | undefined = this.props.commonSettings.amapWebServiceSettings?.find(
      (x) => x.id === this.props.editingAMapWebServiceId
    );

    if (!editingConfig) return;
    this.setState({ editingConfig }, () => {
      this.formRef.current?.resetFields();
      this.formRef.current?.setFieldsValue(editingConfig);
    });
  };

  existingNameValidator = async (_rule: unknown, value: string): Promise<void> => {
    const amapWebServiceSettings: IAMapWebServiceSetting[] | null | undefined =
      this.props.commonSettings?.amapWebServiceSettings;
    if (!amapWebServiceSettings) return;

    const isSettingAlreadyExist: boolean = amapWebServiceSettings.some(
      (x) => x.name === value && x.id !== this.props.editingAMapWebServiceId
    );

    if (isSettingAlreadyExist) throw new Error("Amap Web Service Name is already exist.");
  };

  onFinish = (data: IAMapWebServiceFormData): void => {
    this.saveStatus.start();
    const obj: IAMapWebServiceSetting = {
      id: Guid.create().toString(),
      name: data.name,
      apiKey: data.apiKey
    };

    if (this.props.editingAMapWebServiceId) {
      obj.id = this.props.editingAMapWebServiceId;
      this.props.handleUpdateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "amapWebServiceSettings",
        onSuccess: () => {
          // The ✓ goes up first and the dialog leaves after it — see `SaveStatus`.
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    } else {
      this.props.handleCreateCommonConfig({
        formInputName: this.props.formInputName,
        config: obj,
        configKey: "amapWebServiceSettings",
        onSuccess: () => {
          this.saveStatus.succeed(() => {
            this.props.closeModal();
          });
        },
        onError: () => {
          this.saveStatus.fail();
        },
        onFinal: () => {}
      });
    }
  };

  onFinishFailed = (): void => {};

  deleteSetting = (): void => {
    if (!this.props.editingAMapWebServiceId) {
      this.props.closeModal();
      return;
    }

    if (!this.deleteStatus.canStart) return;
    this.deleteStatus.start();
    this.props.handleDeleteCommonConfig({
      formInputName: this.props.formInputName,
      configId: this.props.editingAMapWebServiceId || "",
      configKey: "amapWebServiceSettings",
      onSuccess: () => {
        // Deleting says its outcome in a toast: the setting is gone and so is this dialog,
        // so there is nothing left on screen for a ✓ to sit on.
        this.deleteStatus.reset();
        this.props.closeModal();
        KMainFunctions.displaySuccessNotification(`Amap Web Service setting has been deleted`);
      },
      onError: () => {
        this.deleteStatus.fail();
      },
      onFinal: () => {
        // A delete the container refuses — the setting is still in use — ends without an
        // outcome of its own, and the button would sit on "Deleting" for good.
        if (this.deleteStatus.current === "loading") this.deleteStatus.reset();
      }
    });
  };

  render(): React.ReactNode {
    return (
      <DefaultModal
        className="config-manager__modal"
        showModal={this.props.showModal}
        closeModal={this.props.closeModal}
        firstHeader="Amap Web Service setting"
        secondHeader="Amap Web Service setting"
        width={600}
        centered={true}
        bodyStyle={{ padding: 0 }}
        footer={
          <ConfigModalFooter
            onDelete={this.props.editingAMapWebServiceId ? this.deleteSetting : undefined}
            deleteTestId="mcpAMapWebServiceSettingDeleteButton"
            submitTestId="mcpAMapWebServiceSettingCreateButton"
            submitLabel={this.props.editingAMapWebServiceId ? "Update" : "Create"}
            status={this.state.saveStatus}
            deleteStatus={this.state.deleteStatus}
            submitForm="configAmapWebServiceForm"
          />
        }
      >
        <Form
          id="configAmapWebServiceForm"
          name="amapWebServiceSetting"
          ref={this.formRef}
          onFinish={this.onFinish}
          onFinishFailed={this.onFinishFailed}
          style={{ height: "100%" }}
        >
          <ConfigModalBody description={this.props.getSectionDescription("mcp_amapwebservicesetting_description")}>
            <ConfigField label="Name" required tooltipContentKey="mcp_amapwebservicesetting_name">
              <Form.Item
                name="name"
                rules={[
                  { required: true, message: "Please enter an Amap Web Service Name." },
                  { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Amap Web Service Name is invalid" },
                  { validator: this.existingNameValidator }
                ]}
              >
                <KuikaInput size="md" data-testid="mcpAMapWebServiceSettingNameInput" autoFocus />
              </Form.Item>
            </ConfigField>
            <ConfigField label="API Key" required tooltipContentKey="mcp_amapwebservicesetting_apikey">
              <Form.Item
                name="apiKey"
                rules={[{ required: true, message: "Please enter an Amap Web Service API Key." }]}
              >
                <KuikaInput size="md" data-testid="mcpAMapWebServiceSettingApiKeyInput" />
              </Form.Item>
            </ConfigField>
          </ConfigModalBody>
        </Form>
      </DefaultModal>
    );
  }
}
